/**
 * Shared recording semantics. Canonical field shape selects the input; explicit
 * recording context distinguishes academic assessments from measurements.
 * Generated native and Functions copies must remain identical to this source.
 */

function primaryFieldType(domain) {
  if (domain?.primary) {
    const primary = domain.primary;
    return primary.active === false || primary.retired === true ? null : primary.type;
  }
  return domain?.fieldType
    || (domain?.defaultInputMethod === 'number' ? 'number' : 'multiChoice');
}

export function isAssessmentDomain(domain) {
  return domain?.capabilities?.recording?.context === 'assessment'
    && domain.capabilities.recording.enabled !== false;
}

/** Central numeric presentation never changes a protected adapter or Programme sheet. */
export function usesConfiguredNumberDisplay(domain) {
  return domain?.runtime?.protected === false && !isAssessmentDomain(domain);
}

export function showDomainNumericMaximum(domain) {
  return !usesConfiguredNumberDisplay(domain) || domain?.listBlocks?.outOf !== false;
}

/** A hidden maximum must still explain why a entered value cannot be saved. */
export function getDomainNumberLimitNotice(domain, value, maximum) {
  if (!usesConfiguredNumberDisplay(domain) || !['number', 'string'].includes(typeof value)
    || (typeof value === 'string' && !value.trim())) return null;
  const limit = positiveFiniteNumber(maximum);
  const number = Number(value);
  return limit != null && Number.isFinite(number) && number > limit
    ? `Enter ${limit} or less.` : null;
}

export function getDomainRecordingType(domain) {
  if (isAssessmentDomain(domain)) return 'assessment';
  const type = primaryFieldType(domain);
  if (type === 'number') return 'number';
  if (['rating', 'star'].includes(type)) return 'number';
  if (['boolean', 'binary'].includes(type)) return 'boolean';
  if (['string', 'text', 'storageRef'].includes(type)) return 'text';
  return 'multiChoice';
}

/**
 * Optional recording tools follow the effective presentation settings. Keeping
 * an existing detail editable prevents a settings change from hiding or deleting
 * an unfinished answer. Configured form questions are independent of these tools.
 */
export function getQuickIssueDetailTools(domain, { note, attachments, editingNote = false } = {}) {
  const surface = domain?.surfaces?.quickIssue;
  const enabled = surface !== false && surface?.enabled !== false;
  return {
    notes: (enabled && surface?.notes !== false) || Boolean(note) || editingNote,
    attachments: (enabled && surface?.attachments !== false)
      || (Array.isArray(attachments) && attachments.length > 0),
  };
}

function positiveFiniteNumber(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/** Maximums must be explicit; a Programme Mark sheet owns its frozen denominator. */
export function getDomainNumericMaximum(domain, override) {
  for (const value of [override, domain?.primary?.maxScore, domain?.maxScore]) {
    const maximum = positiveFiniteNumber(value);
    if (maximum !== null) return maximum;
  }
  return null;
}
